// Import model
import { LineItem } from '../models/LineItem';

// Import base class serializer
import { ChildLineItemSerializer } from './ChildLineItemSerializer';

// Import dependency types (models and enums used in properties)
import { CatalogItem } from '../models/CatalogItem';
import { OtgItemCost } from '../models/OtgItemCost';
import { OtgMeasurement } from '../models/OtgMeasurement';

// Import dependency serializers (for complex types)
import { CatalogItemSerializer } from './CatalogItemSerializer';
import { OtgItemCostSerializer } from './OtgItemCostSerializer';
import { OtgMeasurementSerializer } from './OtgMeasurementSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for LineItem - handles JSON serialization and deserialization with type validation
 */
export class LineItemSerializer {
  /**
   * Deserializes a JSON string into a LineItem instance
   */
  static deserialize(json: string): LineItem {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a LineItem instance
   */
  static fromObject(obj: any): LineItem {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to LineItem');
    }


    const instance: LineItem = {
      description: obj['description'] as string | undefined,
      name: obj['name'] as string | undefined,
      quantity: obj['quantity'] ? OtgMeasurementSerializer.fromObject(obj['quantity']) : undefined as any,
      costs: obj['costs'] ? (obj['costs'] as any[]).map((item: any) =>
        OtgItemCostSerializer.fromObject(item)
      ) : [] as any,
      criteria: obj['criteria'] as string | undefined,
      itemId: obj['itemId'] as string | undefined,
      itemIdName: obj['itemId|Name'] as string | undefined,
      margin: obj['margin'] as number | undefined,
      sku: obj['sku'] as string | undefined,
      source: obj['source'] as string | undefined,
      totalCost: obj['totalCost'] as number | undefined,
      totalPrice: obj['totalPrice'] as number | undefined,
      unitCost: obj['unitCost'] as number | undefined,
      unitPrice: obj['unitPrice'] as number | undefined,
      adjustedQuantity: obj['adjustedQuantity'] ? OtgMeasurementSerializer.fromObject(obj['adjustedQuantity']) : undefined as any,
      wasteFactor: obj['wasteFactor'] as number | undefined,
      children: obj['children'] ? (obj['children'] as any[]).map((item: any) =>
        ChildLineItemSerializer.fromObject(item)
      ) : [] as any,
      actualWasteFactor: obj['actualWasteFactor'] as number | undefined,
      cost: obj['cost'] ? OtgItemCostSerializer.fromObject(obj['cost']) : undefined as any,
      sellingUnit: obj['sellingUnit'] ? OtgMeasurementSerializer.fromObject(obj['sellingUnit']) : undefined as any,
      warnings: obj['warnings'] ? (obj['warnings'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      item: obj['item'] ? CatalogItemSerializer.fromObject(obj['item']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of LineItem instances
   */
  static deserializeArray(json: string): LineItem[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a LineItem instance to a JSON string
   */
  static serialize(instance: LineItem): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a LineItem instance to a plain object
   */
  static toObject(instance: LineItem): any {
    return {
      'description': instance.description,
      'name': instance.name,
      'quantity': instance.quantity ? OtgMeasurementSerializer.toObject(instance.quantity) : undefined,
      'costs': instance.costs?.map((item: any) =>
        OtgItemCostSerializer.toObject(item)
      ),
      'criteria': instance.criteria,
      'itemId': instance.itemId,
      'itemId|Name': instance.itemIdName,
      'margin': instance.margin,
      'sku': instance.sku,
      'source': instance.source,
      'totalCost': instance.totalCost,
      'totalPrice': instance.totalPrice,
      'unitCost': instance.unitCost,
      'unitPrice': instance.unitPrice,
      'adjustedQuantity': instance.adjustedQuantity ? OtgMeasurementSerializer.toObject(instance.adjustedQuantity) : undefined,
      'wasteFactor': instance.wasteFactor,
      'children': instance.children?.map((item: any) =>
        ChildLineItemSerializer.toObject(item)
      ),
      'actualWasteFactor': instance.actualWasteFactor,
      'cost': instance.cost ? OtgItemCostSerializer.toObject(instance.cost) : undefined,
      'sellingUnit': instance.sellingUnit ? OtgMeasurementSerializer.toObject(instance.sellingUnit) : undefined,
      'warnings': instance.warnings?.map((item: any) =>
        item
      ),
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'item': instance.item ? CatalogItemSerializer.toObject(instance.item) : undefined,
    };
  }

  /**
   * Serializes an array of LineItem instances to a JSON string
   */
  static serializeArray(instances: LineItem[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
